import { CheckCircle2, XCircle, Loader2, Clock, Package } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export type ValidationStatus = 'pending' | 'validating' | 'valid' | 'invalid';

export interface ModuleValidationState {
  moduleKey: string;
  moduleName: string;
  status: ValidationStatus;
  errors: string[];
  warnings: string[];
}

interface ValidationProgressProps {
  modules: ModuleValidationState[];
  onRetry?: (moduleKey: string) => void;
}

const statusConfig: Record<
  ValidationStatus,
  { icon: React.ElementType; className: string; label: string }
> = {
  pending: {
    icon: Clock,
    className: 'text-muted-foreground',
    label: 'Pending',
  },
  validating: {
    icon: Loader2,
    className: 'text-primary',
    label: 'Validating...',
  },
  valid: {
    icon: CheckCircle2,
    className: 'text-success',
    label: 'Valid',
  },
  invalid: {
    icon: XCircle,
    className: 'text-destructive',
    label: 'Invalid',
  },
};

export function ValidationProgress({ modules, onRetry }: ValidationProgressProps) {
  const completed = modules.filter(
    (m) => m.status === 'valid' || m.status === 'invalid'
  ).length;
  const total = modules.length;
  const progressPercent = total > 0 ? (completed / total) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* Overall Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Validation Progress</span>
          <span className="font-medium">
            {completed} / {total} modules
          </span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      {/* Module Status List */}
      <ul className="space-y-3">
        {modules.map((module) => {
          const config = statusConfig[module.status];
          const Icon = config.icon;

          return (
            <li
              key={module.moduleKey}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3',
                module.status === 'invalid' && 'border-destructive/50 bg-destructive/5',
                module.status === 'valid' && 'border-success/50 bg-success/5'
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg bg-muted'
                )}
              >
                <Package className="h-4 w-4" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{module.moduleName}</span>
                  <div className={cn('flex items-center gap-1 text-sm', config.className)}>
                    <Icon className={cn('h-4 w-4', module.status === 'validating' && 'animate-spin')} />
                    <span>{config.label}</span>
                  </div>
                </div>

                {/* Errors */}
                {module.errors.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {module.errors.map((error, i) => (
                      <li key={i} className="text-sm text-destructive">
                        • {error}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Warnings */}
                {module.warnings.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {module.warnings.map((warning, i) => (
                      <li key={i} className="text-sm text-warning-foreground">
                        • {warning}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Retry button for failed modules */}
                {module.status === 'invalid' && onRetry && (
                  <button
                    type="button"
                    className="mt-2 text-sm text-primary hover:underline"
                    onClick={() => onRetry(module.moduleKey)}
                  >
                    Retry validation
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
